import * as THREE from 'three';
import { WebGLMultisampleRenderTarget } from 'three';
import geometryUpdates from '@/mixins/geometryUpdates';

const TWEEN = require('@tweenjs/tween.js');

export default {
  mixins: [
    geometryUpdates
  ],
  computed: {
    pdm() {
      return this.$store.state.utils.pdm;
    },
    default_textures() {
      return this.$store.state.sceneSetup.default_textures;
    },
    queryParams() {
      return this.$store.state.utils.queryParams;
    },
    isMobile() {
      return this.$store.getters["utils/returnScreenSize"].includes("mobile");
    }
  },
  methods: {
    /**
     * @function setupRenderer
     * Sets up a THREE  webgl renderer with data from the PDM to render the scene into the canvas
     * Appends the rendered scene on the dom element "scene".
     */
    setupRenderer() {
      const { alpha, antialias, high_resolution, setClearColor } = this.pdm.default_display.scene_defaults.renderer;
      const { precision, shadowMap, shadowMapSoft, shadowMapType } = high_resolution;
      const preserveDrawingBuffer = this.$store.getters["utils/getUrlParameterByName"]("preserveDrawingBuffer") === "true" || this.queryParams.slim === "true";
      const DPR = this.queryParams.slim ? 1 : window.devicePixelRatio || 1;
      const { innerWidth: WW, innerHeight: HH } = window;

      this.$model3D.renderer = new THREE.WebGLRenderer({ alpha, antialias, precision, preserveDrawingBuffer, });
      this.$model3D.renderer.setSize(WW, HH);
      this.$model3D.renderer.setPixelRatio(DPR);
      this.$model3D.renderer.setViewport(0, 0, WW * DPR, HH * DPR);
      this.$model3D.renderer.shadowMap.enabled = shadowMap;
      this.$model3D.renderer.shadowMapSoft = shadowMapSoft;
      this.$model3D.renderer.shadowMap.type = THREE[shadowMapType];

      document.getElementById('scene').appendChild(this.$model3D.renderer.domElement);
    },
    /**
     * @function loadDefaultAssets
     * @return {Array} Promises - Array of promises, each promise represent a call for one texture.
     * Loads the initial textures set in the PDM for a model for all components
     * and saves it into the default_textures object
     * This object is use futher down the flow to apply the textures to the 3D model.
     */
    loadDefaultAssets() {
      const promises = [];
      const components = Object.keys(this.pdm.components);
      components.forEach(componentName => {
        const componentObj = this.pdm.components[componentName];
        promises.push(this.loadDefaultGeometries({componentObj, componentName}))
      });
      return new Promise(resolve => {
        Promise.all(promises).then(resolve);
      });
    },
    /**
     * @function saveTexturePromise
     * @param {Array} promises - Array of promises, each promise represent a call for one texture.
     * @param {Object} componentObj - Object with the component data tha contains the textures to load
     * @param {String} componentName - Name of component that needs textures loaded
     * Saves the texture load promises into the promises array and saves the default textures
     *  into the the default_textures object once they are loaded
     */
    saveTexturePromise(componentObj, componentName) {
      const promises = [];
      if (componentObj.mesh_material && componentObj.mesh_material.textures) {
        const textures = Object.keys(componentObj.mesh_material.textures);
        textures.forEach(i => {
          const texture = componentObj.mesh_material.textures[i];
          const newPromise = new Promise(resolve => {
            this.$model3D.loadTexture({
              image_url: `${this.$store.state.endPoints.customizer.s3SwatchesBucket}assets${texture.image_url}`,
              options: texture.options,
            })
            .then(textureObj => {
              if (this.default_textures[componentName] && this.default_textures[componentName][texture.map_type]) {
                this.$model3D.sceneHelpers.dispose(this.default_textures[componentName][texture.map_type]);
              }
              this.$store.commit("sceneSetup/SAVE_DEFAULT_TEXTURES", {componentName, map_type: i, textureObj});
              resolve();
            })
          });
          promises.push(newPromise);
        });
        return Promise.all(promises)
      }
      return Promise.resolve();
    },
    /**
     * @function responsiveModelDefaults
     * Does the intial positioning, scaling, zoom, etc of the
     * 3D Model based on the rules defined in the product data model
     */
    responsiveModelDefaults() {
      const { responsive_defaults } = this.pdm.default_display.scene_defaults;
      let matchedDefaults = {};
      for (let item of responsive_defaults) {
        let query = "only screen";
        query = `${query} and (min-width: ${item.breakpoint}px)`;
        if (typeof(item.orientation) !== 'undefined') {
          query = `${query} and (orientation: ${item.orientation})`;
        }
        if(window.matchMedia(query).matches) {
          matchedDefaults = item;
        }
      }
      const canvasSize = this.$model3D.renderer.getSize(new THREE.Vector2(window.innerWidth, window.innerHeight));
      if(this.isMobile && 
        canvasSize.x !== canvasSize.y && 
        matchedDefaults.minScaleMobile
      ) {
        const ratio = canvasSize.y/canvasSize.x;
        const min = matchedDefaults.minScaleMobile;
        const max = matchedDefaults.maxScaleMobile;
        const scale = max - (ratio - 1) * (max - min);
        this.$model3D.rootObj.scale.set(
          scale,
          scale,
          scale
        );
      } else {
        this.$model3D.rootObj.scale.set(
          matchedDefaults.scale.x,
          matchedDefaults.scale.y,
          matchedDefaults.scale.z
        );
      }
      this.$model3D.rootObj.position.set(
        matchedDefaults.position.x,
        matchedDefaults.position.y,
        matchedDefaults.position.z
      );
      if (typeof(matchedDefaults.target) !== 'undefined') {
        this.controls.target.set(
          matchedDefaults.target.x,
          matchedDefaults.target.y,
          matchedDefaults.target.z
        );
      }
      this.controls.minDistance = matchedDefaults.minDistance;
      this.controls.maxDistance = matchedDefaults.maxDistance;
    },

    /**
     * @function rotateModel
     * Rotate the model using Tween library.
     */
    rotateModel() {
      this.showRotateMsg = true;
      const tween = new TWEEN.default.Tween(this.$model3D.rootObj.rotation).to({
        y: Math.PI * 2
      }, 2500).delay(400).onComplete(() => {
        setTimeout(()=> {
          this.showRotateMsg = false;
          this.rotateStart();
          this.controls.enableRotate = true;
          this.$eventBus.$emit("component-selected", this.activeComponent);
        }, 1300);
      }).easing(TWEEN.default.Easing.Back.InOut).start();
    },

    /**
     * @function rotateStart
     * Starts the timer. Uses prop autoRotateTimeout for duration.
     */
    rotateStart() {
      if (!this.$model3D.rootObj || typeof this.$model3D.rootObj === "undefined") {
        return;
      }
      if (!this.autoRotateEnabled) return;

      if (this.autoRotate.timer) clearTimeout(this.autoRotate.timer);

      this.autoRotate.timer = setTimeout(() => {
        this.controls.autoRotate = true;
      }, this.autoRotateTimeout);
    },

    /**
     * @function rotateStop
     * @param {Boolean} stopPermanently
     * Check for optional argument stopPermanently to permanently stop autoRotate.
     */
    rotateStop(stopPermanently) {
      if (this.controls.autoRotate) {
        this.controls.autoRotate = false;
      }
      if (!stopPermanently) {
        this.rotateStart();
      }
    },
    async startScreenshotForService() {
      const {size, view, recipe} = this.queryParams
      const logo = this.$model3D.modelObj.children.find(comp => comp.name.includes("insole_logo") && !comp.name.includes("lite"));
      if(view && size && logo) {
        await this.positionScene({view, size});
        this.controls.update();
        this.reapplyUGCUniforms();
        setTimeout(()=>{
          this.takeScreenShot({size, view, recipeId: recipe});
        }, 1000);
      } else if(!logo) {
        setTimeout(()=>{
          this.startScreenshotForService();
        }, 300);
      }
    },
    reapplyUGCUniforms() {
      const ugcComponents = this.$recipe.getUGCComponents();
      if(ugcComponents) {
        for (const componentName of ugcComponents) {
          const componentPDM = this.pdm.components[componentName];
          const materialShader = this.$model3D.sceneHelpers.getSceneObjectByName({ objectName: componentName })?.material?.userData?.shader;
          const normalRepeat = componentPDM.ugc.normal_repeat;
          this.$model3D.sceneHelpers.getSceneObjectByName({
            objectName: componentName
          })?.material.normalMap.repeat.set(normalRepeat,normalRepeat);
          // Code needed for older threeJS can be removed after testings
          if (materialShader) materialShader.uniforms.normalRepeat.value = normalRepeat;
          else console.log( `materialShader missing for ${componentName} ${this.queryParams.recipe}` );
        }
      }
    },
    async positionScene({view, size}){
      return await new Promise(async (resolve, reject) => {
        const pdmRendition = this.pdm.product_images?.renditions?.[size];
        const { width,height,background_color } = pdmRendition;
        const pdmView = this.pdm.product_images?.snapshot_views?.[view];
        // model
        this.$model3D.modelObj.scale.set(pdmView.scale.x,pdmView.scale.y,pdmView.scale.z);
        this.$model3D.modelObj.position.set(pdmView.position.x,pdmView.position.y,pdmView.position.z);
        pdmView.scale.y < 0 && await this.handleMainLeftShoe(pdmView);

        // camera
        this.$model3D.camera.aspect = width/height;
        this.$model3D.camera.position.set(pdmView.camera_position.x,pdmView.camera_position.y,pdmView.camera_position.z);
        this.$model3D.camera.zoom = pdmView.zoom
        this.$model3D.camera.updateProjectionMatrix();

        //renderer
        this.$model3D.renderer.setSize(width, height, true);
        this.$model3D.renderer.setClearColor(Number(background_color), 1);
        resolve();
      });
    },

    async handleMainLeftShoe(pdmView) {
      // Flip Component
      const embroidery = this.$store.getters["personalization/isPersonalizationInComponentRecipe"]();
      embroidery && await new Promise(async (resolve, reject) => {
        const componentMesh = this.$model3D.modelObj.getObjectByName("personalization");
        this.$model3D.modelObj.remove(componentMesh);
        await this.$personalization.applyTextToCanvas({ personalizationComponent: this.$store.getters["personalization/getPersonalizationComponentName"], leftShoe: true }, resolve);
      });
      if(pdmView.components) {
        const components = pdmView.components && Object.keys(pdmView.components);
        components?.forEach((componentName) => {
          const pdmComponent = pdmView.components[componentName];
          const pos = pdmComponent.position;
          const rot = pdmComponent.rotation;
          const component = this.$model3D.modelObj.getObjectByName(componentName);
          if(component) {
            pdmComponent.flipHorizontal && component.scale.set(component.scale.x,-component.scale.y,component.scale.z);
            pos && component.position.set(pos.x,pos.y,pos.z);
            rot && component.rotation.set(rot.x,rot.y,rot.z);
          }
        })
      }
      const ugcComponents = this.$recipe.getUGCComponents();
      if(ugcComponents) {
        for (const componentName of ugcComponents) {
          if(componentName === 'quarters') {
            const texture = await this.handleUGCLeftTextureQuarters();
            this.$model3D.modelObj.getObjectByName("quarters").material.map = texture;
          }
          else if(componentName === 'vamp') {
            this.handleUGCLeftTextureVampSlim();
          }
        }
      }
    },

    handleUGCLeftTextureVampSlim() {
      const geometry = this.$model3D.modelObj.getObjectByName('vamp').geometry.clone();

      const uvAttribute = geometry.attributes.uv;
      for (let i = 0; i < uvAttribute.count; i++) {
          uvAttribute.setX(i, 1 - uvAttribute.getX(i)); // Flip U coordinate
      }
      uvAttribute.needsUpdate = true;
      this.$model3D.modelObj.getObjectByName('vamp').geometry = geometry;
    },

    async takeScreenShot({size, view, recipeId, showSnapShot = false}){
      //let blob = await new Promise(resolve => this.$model3D.renderer.domElement.toBlob(resolve, 'image/jpeg'));
      const renderer = this.$model3D.renderer;
      const scene = this.$model3D.sceneHelpers.getScene()
      const camera = this.$model3D.camera;
      let blob = await this.createScreenshotslim(renderer, scene, camera, 'image/jpeg');
      let retry = 0;
      while ((!blob || blob?.size < 7000) && retry < 10) {
        console.log("Blank blob: canvas may not have been rendered, attempting to regenerat - retry - ", retry);
        await new Promise(r => setTimeout(r, 500));
        // Get blob directly from the scene
        //blob = await new Promise(resolve => this.$model3D.renderer.domElement.toBlob(resolve, 'image/jpeg'));
        // Get blob using WebGLMultisampleRenderTarget(webgl v2) or WebGLRenderTarget (webgl v1)
        blob = await this.createScreenshotslim(renderer, scene, camera, 'image/jpeg');
        retry++;
      }
      if(retry>=10) console.log("Blank blob: Image failed.")
      const data = await this.$store.dispatch("utils/GET_PRODUCT_IMAGES_FROM_SCENE", { blob, recipeId, size, view});
      this.$eventBus.$emit("snapshot-complete", data);
    },
    
    async createScreenshotslim(renderer, scene, camera, imageType = 'image/png') {
      return new Promise((resolve, reject) => {
        const { width, height } = renderer.getSize(new THREE.Vector2());

        // 1. Create a multisampled render target (Three.js r178+ supports samples directly)
        const renderTarget = new THREE.WebGLRenderTarget(width, height, {
          format: THREE.RGBAFormat,
          type: THREE.UnsignedByteType,
          colorSpace: THREE.SRGBColorSpace,
          samples: 4,
        });

        // 2. Render to it
        renderer.setRenderTarget(renderTarget);
        renderer.render(scene, camera);
        renderer.setRenderTarget(null);

        // 3. Read pixels
        const pixelBuffer = new Uint8Array(width * height * 4);
        renderer.readRenderTargetPixels(renderTarget, 0, 0, width, height, pixelBuffer);

        // 4. Flip Y
        const flipped = new Uint8ClampedArray(width * height * 4);
        for (let y = 0; y < height; y++) {
          const src = y * width * 4;
          const dst = (height - y - 1) * width * 4;
          flipped.set(pixelBuffer.subarray(src, src + width * 4), dst);
        }

        // 5. Draw to canvas and convert to blob
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        const imageData = new ImageData(flipped, width, height);
        ctx.putImageData(imageData, 0, 0);

        canvas.toBlob(blob => {
          renderTarget.dispose();
          if (blob) resolve(blob);
          else reject(new Error("Screenshot failed: canvas.toBlob() returned null"));
        }, imageType);
      });
    }
  }
};
